export type AssistantConversationPersistenceCandidate = {
  messageId?: string | null;
  metadata?: Record<string, unknown> | null;
};

const normalizeMessageId = (
  message: AssistantConversationPersistenceCandidate,
): string | null => {
  const messageId =
    typeof message.messageId === "string" ? message.messageId.trim() : "";
  return messageId.length > 0 ? messageId : null;
};

export const selectMessagesPendingConversationPersistence = <
  TMessage extends AssistantConversationPersistenceCandidate,
>(
  messages: readonly TMessage[],
  persistedMessageIds: ReadonlySet<string>,
  pendingMessageIds: ReadonlySet<string>,
): TMessage[] => {
  const selectedMessageIds = new Set<string>();
  return messages.filter((message) => {
    if (
      message.metadata?.transientConversationMessage === true ||
      message.metadata?.assistantResponsePhase === "deterministic-preview"
    ) {
      return false;
    }
    const messageId = normalizeMessageId(message);
    if (!messageId) {
      return false;
    }
    if (
      persistedMessageIds.has(messageId) ||
      pendingMessageIds.has(messageId) ||
      selectedMessageIds.has(messageId)
    ) {
      return false;
    }
    selectedMessageIds.add(messageId);
    return true;
  });
};

export const markMessagesPendingConversationPersistence = (
  messages: readonly AssistantConversationPersistenceCandidate[],
  pendingMessageIds: Set<string>,
): string[] => {
  const markedMessageIds: string[] = [];
  for (const message of messages) {
    const messageId = normalizeMessageId(message);
    if (!messageId || pendingMessageIds.has(messageId)) {
      continue;
    }
    pendingMessageIds.add(messageId);
    markedMessageIds.push(messageId);
  }
  return markedMessageIds;
};

export const clearPendingConversationPersistenceMessageIds = (
  pendingMessageIds: Set<string> | null | undefined,
  messageIds: readonly string[],
): void => {
  if (!pendingMessageIds) {
    return;
  }
  for (const messageId of messageIds) {
    pendingMessageIds.delete(messageId);
  }
};
